import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, Box, Typography, Button, IconButton } from "@mui/material";
import { Close as CloseIcon, ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { DiscountCode } from "../types";

function DiscountCodeGiftDialog() {
  const { playerToken } = useAuth();
  const [gift, setGift] = useState<DiscountCode | null>(null);
  const [open, setOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPendingGift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingGift = async () => {
    try {
      const response = await apiRequest("/api/discount-codes/my-pending-gift", {
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setGift(data);
          setOpen(true);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      }
    } catch (error) {
      console.error("Error fetching pending discount code gift:", error);
    }
  };

  const handleCopy = () => {
    if (!gift) return;
    navigator.clipboard.writeText(gift.code);
    setCopied(true);
  };

  const handleClose = async () => {
    setOpen(false);
    if (!gift) return;
    try {
      await apiRequest(`/api/discount-codes/${gift.id}/acknowledge`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
    } catch (error) {
      console.error("Error acknowledging discount code gift:", error);
    }
  };

  if (!gift) return null;

  const label =
    gift.discountType === "percentage"
      ? `${gift.discountValue}% OFF`
      : `EUR ${gift.discountValue} OFF`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)",
          color: "white",
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{ position: "absolute", right: 8, top: 8, color: "white", zIndex: 10 }}
      >
        <CloseIcon />
      </IconButton>

      {showConfetti && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {[...Array(30)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: "-10px",
                left: `${Math.random() * 100}%`,
                width: "10px",
                height: "10px",
                backgroundColor: ["#FFD700", "#FF6B6B", "#4ECDC4", "#95E1D3", "#F38181"][
                  Math.floor(Math.random() * 5)
                ],
                animation: `fall ${2 + Math.random() * 3}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`,
                "@keyframes fall": {
                  "0%": { transform: "translateY(0) rotate(0deg)", opacity: 1 },
                  "100%": {
                    transform: `translateY(120vh) rotate(${360 + Math.random() * 360}deg)`,
                    opacity: 0,
                  },
                },
              }}
            />
          ))}
        </Box>
      )}

      <DialogContent
        sx={{
          textAlign: "center",
          py: { xs: 3, sm: 6 },
          px: { xs: 2, sm: 4 },
          pt: { xs: 5, sm: 6 },
          position: "relative",
          zIndex: 2,
          maxHeight: "85dvh",
          overflowY: "auto",
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            mb: 1,
            fontSize: { xs: "1.5rem", sm: "2.5rem" },
            textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            animation: "bounce 0.6s ease-in-out",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-10px)" },
            },
          }}
        >
          🎁 You've Got a Gift!
        </Typography>

        <Typography sx={{ mb: 4, opacity: 0.95, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
          A voucher code just for you
        </Typography>

        <Box
          sx={{
            display: "inline-block",
            background: "rgba(255,255,255,0.15)",
            border: "2px dashed rgba(255,255,255,0.6)",
            borderRadius: 2,
            px: 4,
            py: 3,
            mb: 3,
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.03)" },
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "monospace",
              fontWeight: "bold",
              letterSpacing: 3,
              fontSize: { xs: "1.75rem", sm: "2.5rem" },
            }}
          >
            {gift.code}
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: "bold", fontSize: { xs: "1.25rem", sm: "1.75rem" }, mb: 3 }}>
          {label}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
          Save this code somewhere safe — you'll need it the next time you register for a
          tournament or season. It can only be used once.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{
              color: "white",
              borderColor: "white",
              "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            {copied ? "Copied!" : "Copy Code"}
          </Button>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              backgroundColor: "white",
              color: "#ec4899",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
            }}
          >
            Got it!
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default DiscountCodeGiftDialog;
